import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    FormControlLabel,
    Checkbox,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { useAdmin } from "../../../hooks/useAdmin";
import useCommon from "../../../hooks/useCommon";
import { httpClient } from "../../../apiClient/httpClient";
import { tokenStorage } from "../../../utils/tokenStorage.utils";
import GlobalAlert from "../../../components/common/alert/GlobalAlert.jsx";
import THEME from "../../../constants/theme";
import deviceUtils from "../../../utils/device.utils";

import BrandLogo from "../../../components/common/BrandLogo";


/* ── Shared input field style ─────────────────────────────────────────────── */
function inputSx(primary) {
    return {
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            minHeight: 46,
            fontSize: 14,
            bgcolor: "#fff",
            "& fieldset": { borderColor: "#e2e8f0" },
            "&:hover fieldset": { borderColor: "#94a3b8" },
            "&.Mui-focused fieldset": { borderColor: primary, borderWidth: 2 },
        },
        "& input[type=password]::-ms-reveal": { display: "none" },
        "& input[type=password]::-ms-clear": { display: "none" },
    };
}

/* ── Login form (shared between mobile & desktop) ─────────────────────────── */
function LoginForm({ loginData, showPassword, formError, onUserChange, onPasswordChange,
    onTogglePassword, onSubmit, onForgotOpen, onTermsOpen, onRememberChange, navigate }) {
    const primary = THEME.colors.primary;

    return (
        <form
            onSubmit={onSubmit}
            noValidate
        >
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: "primary.main", fontWeight: 700, fontSize: 11, letterSpacing: 1.6, mb: 1 }}>YOUR LEARNING SPACE</Typography>
                <Typography component="h1" sx={{ fontSize: { xs: 27, sm: 30 }, fontWeight: 700, color: "text.primary", letterSpacing: -1, lineHeight: 1.2 }}>Welcome back.</Typography>
                <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 1 }}>Sign in and pick up where you left off.</Typography>
            </Box>

            {/* Error banner */}
            {formError && (
                <Box role="alert" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5, px: 1.75, py: 1.1, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px" }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#ef4444", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, color: "#dc2626" }}>{formError}</Typography>
                </Box>
            )}

            {/* Email */}
            <Typography component="label" htmlFor="login-username" sx={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1e293b", mb: 0.8 }}>
                Username / Email address
            </Typography>
            <TextField
                id="login-username"
                name="username"
                autoComplete="username"
                placeholder="Your username or email"
                fullWidth
                value={loginData.user}
                onChange={onUserChange}
                error={!!formError && !loginData.user}
                sx={{ mb: 2, ...inputSx(primary) }}
                InputProps={{
                    sx: { py: 0.2 },
                    startAdornment: (
                        <InputAdornment position="start">
                            <EmailOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Password */}
            <Typography component="label" htmlFor="login-password" sx={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1e293b", mb: 0.8 }}>
                Password
            </Typography>
            <TextField
                id="login-password"
                name="password"
                autoComplete="current-password"
                placeholder="Your password"
                type={showPassword ? "text" : "password"}
                fullWidth
                value={loginData.password}
                onChange={onPasswordChange}
                error={!!formError && !loginData.password}
                sx={{ mb: 1.5, ...inputSx(primary) }}
                InputProps={{
                    sx: { py: 0.2 },
                    startAdornment: (
                        <InputAdornment position="start">
                            <LockOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} size="small" onClick={onTogglePassword} edge="end" sx={{ mr: -0.5 }}>
                                {showPassword
                                    ? <Visibility sx={{ fontSize: 18, color: "#94a3b8" }} />
                                    : <VisibilityOff sx={{ fontSize: 18, color: "#94a3b8" }} />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            {/* Remember me + Forgot password row */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
                {(
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={loginData.remember}
                                onChange={onRememberChange}
                                size="small"
                                sx={{ color: "#cbd5e1", "&.Mui-checked": { color: primary } }}
                            />
                        }
                        label={<Typography sx={{ fontSize: 14, color: "#64748b" }}>Remember me</Typography>}
                        sx={{ m: 0 }}
                    />
                )}
                <Link
                    component="button"
                    type="button"
                    underline="none"
                    onClick={onForgotOpen}
                    sx={{ fontSize: "0.8rem", fontWeight: 600, color: primary, ml: "auto", "&:hover": { textDecoration: "underline", bgcolor: "transparent" } }}
                >
                    Forgot password?
                </Link>
            </Box>

            {/* Sign In */}
            <Button
                fullWidth
                variant="contained"
                type="submit"
                sx={{
                    py: 1.5, fontWeight: 700, fontSize: 14, textTransform: "none",
                    borderRadius: "8px", bgcolor: THEME.colors.primary, letterSpacing: "0.01em",
                    boxShadow: "none",
                    "&:hover": { bgcolor: THEME.colors.primaryHover, boxShadow: "none" },
                }}
            >
                Sign in to your workspace
            </Button>

            {/* Sign up */}
            <Typography sx={{ textAlign: "center", mt: 3, pt: 2.5, borderTop: "1px solid", borderColor: "divider", fontSize: 14, color: "#64748b" }}>
                Don't have an account?{" "}
                <Link
                    component="button" type="button" underline="none"
                    onClick={() => navigate("/user/register")}
                    sx={{ fontWeight: 700, color: THEME.colors.primary, "&:hover": { textDecoration: "underline", bgcolor: "transparent" } }}
                >
                    Sign up
                </Link>
            </Typography>

            {/* Terms */}
            <Typography sx={{ textAlign: "center", mt: 1.5, fontSize: "0.75rem", color: "#64748b" }}>
                By signing in you agree to our{" "}
                <Link
                    component="button" type="button" underline="hover"
                    onClick={onTermsOpen}
                    sx={{ color: "#64748b", "&:hover": { bgcolor: "transparent" } }}
                >
                    Terms and Conditions
                </Link>
            </Typography>
        </form>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function LoginPage() {
    const navigate = useNavigate();
    const { login, user } = useAuth();
    const { setPermissionsAPI } = useAdmin();
    const { showLoader, hideLoader } = useCommon();

    const [alert, setAlert] = useState({ open: false, type: "", message: "" });
    const [loginData, setLoginData] = useState({ user: "", password: "", remember: false });
    const [showPassword, setShowPassword] = useState(false);
    const [formError, setFormError] = useState("");
    const [openForgot, setOpenForgot] = useState(false);
    const [openTerms, setOpenTerms] = useState(false);

    const validate = () => {
        if (!loginData.user || !loginData.password) {
            setFormError("Username and password are required.");
            return false;
        }
        setFormError("");
        return true;
    };

    const handleUserChange = (e) => {
        setLoginData((s) => ({ ...s, user: e.target.value }));
        if (formError) setFormError("");
    };

    const handlePasswordChange = (e) => {
        setLoginData((s) => ({ ...s, password: e.target.value }));
        if (formError) setFormError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        try {
            const device_id = deviceUtils.getDeviceId();
            const { device_type, device_info } = deviceUtils.getDeviceInfo();
            const ip_address = await deviceUtils.getPublicIp();
            const payload = { ...loginData, device_id, device_type, device_info, ip_address };

            const resultAction = await login(payload);
            if (!resultAction?.type?.endsWith("/fulfilled")) {
                setAlert({ open: true, type: "error", message: "Login failed" });
                return;
            }

            const userStr = tokenStorage.getUserToken();
            const userObj = userStr ? JSON.parse(userStr) : null;
            const userId = userObj?.id || userObj?.userId;

            showLoader("Fetching permissions...");
            const res = await httpClient.fetchPermissionByUserId(userId);

            if (res?.data?.response?.length) {
                setPermissionsAPI(res.data.response);
                navigate("/", { replace: true });
            } else {
                navigate("/unauthorized", { replace: true });
            }
        } catch {
            setAlert({ open: true, type: "error", message: "Login failed" });
            navigate("/unauthorized", { replace: true });
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        if (user && (user.id || user.userId)) navigate("/", { replace: true });
    }, [user, navigate]);


    const formProps = {
        loginData, showPassword, formError,
        onUserChange: handleUserChange,
        onPasswordChange: handlePasswordChange,
        onTogglePassword: () => setShowPassword((s) => !s),
        onRememberChange: (e) => setLoginData((s) => ({ ...s, remember: e.target.checked })),
        onSubmit: handleSubmit,
        onForgotOpen: () => setOpenForgot(true),
        onTermsOpen: () => setOpenTerms(true),
        navigate,
    };

    return (
        <Box sx={{ minHeight: "100dvh", bgcolor: THEME.colors.dark, color: "#fff", position: "relative", isolation: "isolate", display: "flex", flexDirection: "column" }}>
            <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: -1, pointerEvents: "none" }}>
                <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,.09) 1px, transparent 1px)", backgroundSize: "28px 28px", maskImage: "linear-gradient(to bottom, transparent, black)" }} />
                <Box sx={{ position: "absolute", width: 680, height: 680, top: "12%", left: "-320px", border: "1px solid rgba(116,238,250,.16)", borderRadius: "50%", boxShadow: "0 0 0 75px rgba(116,238,250,.025), 0 0 0 150px rgba(116,238,250,.02)" }} />
                <Box sx={{ position: "absolute", width: 500, height: 500, right: -240, bottom: -150, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,87,255,.26), transparent 70%)" }} />
            </Box>
            <Box component="header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, px: { xs: 2.5, md: 5 }, py: 2.5, borderBottom: "1px solid rgba(255,255,255,.1)" }}>
                <BrandLogo light compact />
                <Typography sx={{ display: { xs: "none", sm: "block" }, color: "#B4C7DF", fontSize: 13 }}>A little progress. Every day.</Typography>
            </Box>
            <Box component="main" sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
                <Box sx={{ width: "100%", maxWidth: 460, bgcolor: "background.paper", color: "text.primary", borderRadius: 2.5, boxShadow: "0 24px 80px rgba(0,0,0,.25)", overflow: "hidden" }}>
                    <Box sx={{ height: 4, background: `linear-gradient(90deg, ${THEME.colors.primary}, ${THEME.colors.secondary})` }} />
                    <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
                        <LoginForm {...formProps} />
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: { xs: 2, sm: 3 }, mt: 3, color: "#B4C7DF" }}>
                    {["Explore courses", "Build your skills", "Track your progress"].map((label) => (
                        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box aria-hidden="true" sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: THEME.colors.secondary }} />
                            <Typography sx={{ fontSize: 12 }}>{label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
            <Box component="footer" sx={{ px: 2, pb: 2.5, textAlign: "center" }}>
                <Typography sx={{ fontSize: 11, color: "#B4C7DF", letterSpacing: 1 }}>ESAINEX LMS ? LEARN. GROW. ACHIEVE.</Typography>
            </Box>
            <GlobalAlert alert={alert} setAlert={setAlert} />
            <ForgotDialog open={openForgot} onClose={() => setOpenForgot(false)} />
            <TermsDialog open={openTerms} onClose={() => setOpenTerms(false)} />
        </Box>
    );
}

/* ── Dialog components ───────────────────────────────────────────────────── */
function ForgotDialog({ open, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                Forgot Password
                <IconButton aria-label="Close dialog" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography>Please contact your system administrator or IT support team to reset your password.</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function TermsDialog({ open, onClose }) {
    const terms = [
        "Authorized access only: this platform is for registered users with valid credentials.",
        "Account security: you are responsible for safeguarding your login and must not share credentials.",
        "Acceptable use: use the system only for legitimate learning and work-related activities.",
        "Prohibited behaviour: harassment, misuse, or disruption of services is forbidden.",
        "Intellectual property: course content is owned or licensed and must not be redistributed without permission.",
        "Privacy: personal data is processed according to your organization's privacy policy.",
        "Data retention: usage and progress data may be logged and retained for administrative purposes.",
        "Compliance: you must comply with applicable laws and organizational policies while using the LMS.",
        "Consequences: violations may result in access suspension, disciplinary action, or legal remedies.",
        "Support: contact your administrator or IT support for issues, access requests, or policy questions.",
    ];
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Terms and Conditions
                <IconButton aria-label="Close dialog" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box component="ol" sx={{ pl: 3 }}>
                    {terms.map((text, i) => (
                        <Box component="li" key={i} sx={{ mb: 1 }}>
                            <Typography variant="body2">{text}</Typography>
                        </Box>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
